/**
 * Shared pieces of the editor-to-engine lowerings (utils/entry-window.ts,
 * utils/lower-trade-managers.ts): passes that rewrite the raw editor graph
 * into nodes the engine already knows, before serialization.
 */

export interface LooseNode {
  id: string
  type?: string
  data?: unknown
  parentId?: string
  position?: { x: number; y: number }
}

export interface LooseEdge {
  id: string
  source: string
  target: string
  sourceHandle?: string | null
  targetHandle?: string | null
  data?: unknown
}

/**
 * A collapsed subnet's stand-in for a wire crossing its border, rerouted to the
 * subnet node. Lowerings read only canonical wires, so they act the same
 * whether a subnet is open or collapsed (as pruneDisabledStreams does).
 */
export const isProxyEdge = (edge: LooseEdge): boolean =>
  (edge.data as { proxy?: boolean } | undefined)?.proxy === true

export const socketKey = (nodeId: string, handle: string | null | undefined) =>
  `${nodeId}\u0000${handle ?? ''}`

/** A node's settings as a record, from either stored shape (record or `{ name, value }` list). */
export function readParams(node: LooseNode): Record<string, unknown> {
  const raw = (node.data as { parameters?: unknown } | undefined)?.parameters
  if (Array.isArray(raw)) {
    const out: Record<string, unknown> = {}
    for (const p of raw)
      if (p && typeof p === 'object' && 'name' in p)
        out[(p as { name: string }).name] = (p as { value?: unknown }).value
    return out
  }
  return (raw ?? {}) as Record<string, unknown>
}

/** `base`, or `base-2`, `base-3`... whichever is free; claims it in `taken`. */
export function freshId(base: string, taken: Set<string>): string {
  let id = base
  for (let n = 2; taken.has(id); n++) id = `${base}-${n}`
  taken.add(id)
  return id
}

/**
 * A pass that rewrites the editor graph into nodes the engine knows: every
 * node of one type (a compound's `lower`), or every node with one facet. It
 * reads canonical wires only (toEngineView drops a collapsed subnet's proxy
 * wires first) and returns `owners`: each id it claimed, to the node that
 * made it, so readiness and run errors point at something on the canvas.
 */
export type Lowering = <N extends LooseNode, E extends LooseEdge>(
  nodes: N[],
  edges: E[],
) => { nodes: N[]; edges: E[]; owners: Map<string, string> }
